import React from 'react';
import { View, StyleSheet, Pressable, Platform } from 'react-native';
import { AnchorRect, useAnchoredPosition } from '@/hooks/common/useAnchoredPosition';
import { useAdminColors } from '@/hooks/admin/useAdminColors';

export const AnchoredDropdown = ({
  isOpen,
  anchor,
  width = 240,
  maxHeight = 320,
  children,
  onRequestClose,
  disableBackdrop = false,
}: {
  isOpen: boolean;
  anchor: AnchorRect | null;
  width?: number;
  maxHeight?: number;
  children: React.ReactNode;
  onRequestClose?: () => void;
  disableBackdrop?: boolean;
}) => {
  const { admin } = useAdminColors();
  const { top, bottom, left, maxHeight: mh, placement } = useAnchoredPosition(anchor, { width, maxHeight });
  if (!isOpen || !anchor) return null;

  const verticalStyle = placement === 'above' && bottom !== undefined ? { bottom } : { top };

  const content = (
    <View style={styles.portalRoot} pointerEvents="box-none">
      {!disableBackdrop && <Pressable style={styles.backdrop} onPress={onRequestClose} />}
      <View style={[styles.panel, { left, width, maxHeight: mh, backgroundColor: admin.selector.dropdownBg, borderColor: admin.selector.border }, verticalStyle] }>
        {children}
      </View>
    </View>
  );

  if (Platform.OS === 'web') {
    const { createPortal } = require('react-dom');
    const body = (globalThis as any)?.document?.body;
    if (body) {
      // The dropdown is portaled to <body>, but React replays events up the
      // *component* tree, not the DOM tree — so a click inside the panel would
      // otherwise bubble to ancestor pressables (e.g. a row's onPress) and
      // trigger them. Swallow clicks at the portal boundary. The backdrop's own
      // onPress (close) still fires first, before this stops further bubbling.
      const stop = (e: any) => e.stopPropagation();
      const guarded = React.createElement(
        'div',
        { onClick: stop, onMouseDown: stop },
        content,
      );
      return createPortal(guarded, body);
    }
    return null;
  }

  return content;
};

const styles = StyleSheet.create({
  portalRoot: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10000,
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
  },
  panel: {
    position: 'absolute',
    borderWidth: 1,
    borderRadius: 8,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOpacity: 0.24,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 8 },
    elevation: 20,
    zIndex: 999999,
  },
});
